/**
 * Display helpers shared by the API (names it sends) and the web app (names and codes it shows).
 * Pure string functions, no locale or DOM.
 *
 * Imported data arrives in every casing: "MICHAEL AKERMAN", "calum adamson", "PortTalbot". These
 * helpers tidy what was clearly typed in one case, and leave deliberate mixed case alone
 * ("McDonald", "DeVito", "Mckeen" stay exactly as entered).
 */

/** Lower-case particles inside a surname ("Dias de Castilho"); never the first word. */
const PARTICLES = new Set([
  'de',
  'da',
  'das',
  'do',
  'dos',
  'du',
  'van',
  'von',
  'der',
  'den',
  'la',
  'le',
]);

/** Surnames that start "Mac" but aren't Mac + name ("Mack", "Macey", "Machin"). */
const MAC_EXCEPTIONS = new Set([
  'mace',
  'macey',
  'machin',
  'machado',
  'macias',
  'mack',
  'mackie',
  'macklin',
  'mackey',
  'macon',
  'macro',
  'macrae',
]);

function capitalise(part: string): string {
  if (part === '') return part;
  return part.charAt(0).toUpperCase() + part.slice(1).toLowerCase();
}

/** One single-case word (no spaces or hyphens) in name case: "MCLEAN" → "McLean", "o'neill" → "O'Neill". */
function nameCaseWord(word: string, first: boolean): string {
  const lower = word.toLowerCase();
  if (!first && PARTICLES.has(lower)) return lower;
  // O'Neill, D'Arcy: capitalise after an apostrophe when the prefix is one letter.
  const apos = /^([a-z])['’]([a-z].*)$/.exec(lower);
  if (apos) return `${apos[1]!.toUpperCase()}'${capitalise(apos[2]!)}`;
  if (/^mc[a-z]{2,}$/.test(lower)) return `Mc${capitalise(lower.slice(2))}`;
  if (/^mac[a-z]{3,}$/.test(lower) && !MAC_EXCEPTIONS.has(lower)) {
    return `Mac${capitalise(lower.slice(3))}`;
  }
  return capitalise(lower);
}

/** True when the word has letters and they are all one case (so we may re-case it). */
function isSingleCase(word: string): boolean {
  if (!/[a-z]/i.test(word)) return false;
  return word === word.toUpperCase() || word === word.toLowerCase();
}

/**
 * A person's name in proper case, for display. Each word typed all upper or all lower case is
 * re-cased (Mc/Mac, O', hyphens and double-barrelled names handled); mixed-case words are kept.
 *
 *   "MICHAEL AKERMAN" → "Michael Akerman"     "calum adamson" → "Calum Adamson"
 *   "SARRA MCLEAN"    → "Sarra McLean"        "o'neill"        → "O'Neill"
 *   "SMITH-JONES"     → "Smith-Jones"         "DIAS DE CASTRO" → "Dias de Castro"
 *   "Mckeen"          → "Mckeen" (kept)
 */
export function formatPersonName(name: string | null | undefined): string {
  if (!name) return '';
  const words = name.trim().split(/\s+/);
  return words
    .map((word, i) => {
      if (!isSingleCase(word)) return word;
      // Single letters ("J") and initials ("J.") just go upper case.
      if (/^[a-z]\.?$/i.test(word)) return word.toUpperCase();
      return word
        .split('-')
        .map((part, j) => nameCaseWord(part, i === 0 || j > 0))
        .join('-');
    })
    .join(' ');
}

/** "Sam Kaur" from the stored parts: the preferred name wins over the given name. */
export function personDisplayName(
  given: string | null | undefined,
  family: string | null | undefined,
  preferred?: string | null,
): string {
  const first = preferred?.trim() || given?.trim() || '';
  return formatPersonName(`${first} ${family?.trim() ?? ''}`.trim());
}

/**
 * A site name for display: splits run-together words ("PortTalbot" → "Port Talbot") and
 * re-cases names typed all in capitals ("STAFFORD" → "Stafford"). Codes are never names: show
 * the name, and the code only as secondary mono text where someone needs it.
 */
export function formatSiteName(name: string | null | undefined): string {
  if (!name) return '';
  let text = name.trim().replace(/([a-z])([A-Z])/g, '$1 $2');
  if (text === text.toUpperCase() && /[A-Z]{2}/.test(text)) {
    text = text
      .split(/\s+/)
      .map((w) => (w.length <= 2 ? w : capitalise(w)))
      .join(' ');
  }
  return text;
}

/**
 * Labels for raw codes that reach the screen (enum values, reason codes, queue keys). Anything
 * not listed falls back to `humaniseCode`, so add an entry only when the plain conversion reads
 * wrong.
 */
export const CODE_LABELS: Readonly<Record<string, string>> = {
  // Absence and payroll categories.
  sick_pay: 'Sick pay',
  ssp: 'Statutory sick pay',
  authorised_paid: 'Authorised (paid)',
  authorised_unpaid: 'Authorised (unpaid)',
  unauthorised: 'Unauthorised',
  per_policy: 'As per policy',
  shared_parental: 'Shared parental leave',
  family_leave: 'Family leave',
  emergency_leave: 'Emergency leave',
  unpaid_leave: 'Unpaid leave',
  early_leaving_unpaid: 'Left early (unpaid)',
  awol: 'AWOL',
  toil: 'Time off in lieu',
  // Attendance resolution reasons.
  forgot_to_clock: 'Forgot to clock',
  shift_change: 'Shift change',
  customer_delivery: 'Customer delivery',
  system_error: 'Clock or system fault',
  no_site_access: 'Couldn’t get on site',
  // Workflow statuses.
  in_progress: 'In progress',
  on_hold: 'On hold',
  not_started: 'Not started',
  dry_run: 'Dry run (nothing is sent)',
  // Holiday disagreement queue.
  days_differ: 'Days differ',
  bank_holidays_added: 'Bank holidays added',
  oldRemaining: 'Old system remaining',
  aroaRemaining: 'Aroo remaining',
  arooRemaining: 'Aroo remaining',
  oldTaken: 'Old system taken',
  arooTaken: 'Aroo taken',
  // Systems.
  dt_spar: 'the old system',
  colosseum: 'Colosseum',
  autopart: 'Autopart',
  hgv: 'HGV',
  lgv: 'LGV',
  mot: 'MOT',
  riddor: 'RIDDOR',
  hr: 'HR',
  ni: 'NI',
  po: 'PO',
  vor: 'VOR',
  pin: 'PIN',
  epod: 'ePOD',
};

/** Words that keep their capitals inside a humanised code ("hgv_licence" → "HGV licence"). */
const ACRONYMS = new Set([
  'hgv',
  'lgv',
  'mot',
  'riddor',
  'hr',
  'ni',
  'po',
  'vor',
  'pin',
  'ssp',
  'smp',
  'spp',
  'awol',
  'toil',
  'id',
  'uk',
  'it',
  'pdf',
  'xlsx',
  'oc',
  'dc',
  'gps',
  'cctv',
]);

/**
 * "forgot_to_clock" → "Forgot to clock", "oldRemaining" → "Old remaining", "IN_PROGRESS" →
 * "In progress". Sentence case, acronyms kept.
 */
export function humaniseCode(code: string | null | undefined): string {
  if (!code) return '';
  const words = code
    .trim()
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .split(/[\s_.\-/]+/)
    .filter(Boolean)
    .map((w) => w.toLowerCase());
  if (words.length === 0) return '';
  return words
    .map((w, i) => {
      if (ACRONYMS.has(w)) return w.toUpperCase();
      return i === 0 ? capitalise(w) : w;
    })
    .join(' ');
}

/**
 * The label for a code: the caller's own map first, then the shared `CODE_LABELS`, then the
 * humanised code. Never shows snake_case or camelCase to a person.
 */
export function formatCode(
  code: string | null | undefined,
  labels?: Readonly<Record<string, string>>,
): string {
  if (!code) return '';
  return labels?.[code] ?? CODE_LABELS[code] ?? humaniseCode(code);
}
